/**
 * Centralized configuration for award category-specific UI strings and behavior.
 *
 * USASpending has 6 main award categories, each with different terminology and
 * tab visibility requirements:
 *
 * | Category        | Has Subawards | Has Orders | Recipient Label     |
 * |-----------------|---------------|------------|---------------------|
 * | idv             | Yes           | Yes        | Prime Contractor    |
 * | contract        | Yes           | No         | Prime Contractor    |
 * | grant           | Yes           | No         | Recipient           |
 * | direct payment  | No            | No         | Recipient           |
 * | loans           | No            | No         | Recipient           |
 * | insurance       | No            | No         | Recipient           |
 *
 * See docs/usaspending-award-types.md for full documentation.
 */

export interface CategoryConfig {
  /** Label for the recipient/contractor in this category */
  recipientLabel: string
  /** Title for recipient details sidebar widget */
  recipientDetailsTitle: string
  /** Label for the modifications/transactions tab */
  transactionsTabLabel: string
  /** Whether this category can have subawards */
  hasSubawards: boolean
  /** Whether this category can have child orders (IDV only) */
  hasOrders: boolean
  /** Label for the orders tab (IDV only) */
  ordersTabLabel: string | null
  /** Label for the primary amount in header data grid */
  primaryAmountLabel: string
  /** Label for the type field in header data grid */
  typeLabel: string
  /** Label for subawardee/subcontractor/subgrantee */
  subawardeeLabel: string
  /** Title for subawardee details section */
  subawardeeDetailsTitle: string
  /** Label for the prime award recipient/contractor */
  primeLabel: string
}

const contractConfig: CategoryConfig = {
  recipientLabel: 'Prime Contractor',
  recipientDetailsTitle: 'Prime Contractor Details',
  transactionsTabLabel: 'Transactions/Mods',
  hasSubawards: true,
  hasOrders: false,
  ordersTabLabel: null,
  primaryAmountLabel: 'Total Obligated',
  typeLabel: 'Contract Type',
  subawardeeLabel: 'Subcontractor',
  subawardeeDetailsTitle: 'Subcontractor Details',
  primeLabel: 'Prime Contractor',
}

const idvConfig: CategoryConfig = {
  recipientLabel: 'Prime Contractor',
  recipientDetailsTitle: 'Prime Contractor Details',
  transactionsTabLabel: 'Transactions/Mods',
  hasSubawards: true,
  hasOrders: true,
  ordersTabLabel: 'Orders',
  primaryAmountLabel: 'Contract Ceiling',
  typeLabel: 'IDV Type',
  subawardeeLabel: 'Subcontractor',
  subawardeeDetailsTitle: 'Subcontractor Details',
  primeLabel: 'Prime Contractor',
}

const grantConfig: CategoryConfig = {
  recipientLabel: 'Recipient',
  recipientDetailsTitle: 'Recipient Details',
  transactionsTabLabel: 'Transactions/Mods',
  hasSubawards: true,
  hasOrders: false,
  ordersTabLabel: null,
  primaryAmountLabel: 'Federal Obligation',
  typeLabel: 'Grant Type',
  subawardeeLabel: 'Subgrantee',
  subawardeeDetailsTitle: 'Subgrantee Details',
  primeLabel: 'Prime Recipient',
}

const loanConfig: CategoryConfig = {
  recipientLabel: 'Recipient',
  recipientDetailsTitle: 'Recipient Details',
  transactionsTabLabel: 'Transactions/Mods',
  hasSubawards: false,
  hasOrders: false,
  ordersTabLabel: null,
  primaryAmountLabel: 'Loan Value',
  typeLabel: 'Loan Type',
  subawardeeLabel: 'Sub-Recipient',
  subawardeeDetailsTitle: 'Sub-Recipient Details',
  primeLabel: 'Prime Recipient',
}

const directPaymentConfig: CategoryConfig = {
  recipientLabel: 'Recipient',
  recipientDetailsTitle: 'Recipient Details',
  transactionsTabLabel: 'Transactions/Mods',
  hasSubawards: false,
  hasOrders: false,
  ordersTabLabel: null,
  primaryAmountLabel: 'Total Disbursed',
  typeLabel: 'Payment Type',
  subawardeeLabel: 'Sub-Recipient',
  subawardeeDetailsTitle: 'Sub-Recipient Details',
  primeLabel: 'Prime Recipient',
}

const defaultConfig: CategoryConfig = {
  recipientLabel: 'Recipient',
  recipientDetailsTitle: 'Recipient Details',
  transactionsTabLabel: 'Transactions/Mods',
  hasSubawards: false,
  hasOrders: false,
  ordersTabLabel: null,
  primaryAmountLabel: 'Award Amount',
  typeLabel: 'Award Type',
  subawardeeLabel: 'Sub-Recipient',
  subawardeeDetailsTitle: 'Sub-Recipient Details',
  primeLabel: 'Prime Recipient',
}

/**
 * Map of award category to UI configuration.
 * Categories not in this map use defaultConfig.
 */
const categoryConfigMap: Record<string, CategoryConfig> = {
  contract: contractConfig,
  idv: idvConfig,
  grant: grantConfig,
  'direct payment': directPaymentConfig,
  loans: loanConfig,
  insurance: defaultConfig,
}

/**
 * Get the UI configuration for a given award category.
 * Falls back to defaultConfig for unknown categories.
 */
export function getCategoryConfig(
  category: string | null | undefined,
): CategoryConfig {
  if (!category) return defaultConfig
  const normalizedCategory = category.toLowerCase().trim()
  return categoryConfigMap[normalizedCategory] ?? defaultConfig
}

/**
 * Check if a category supports subawards.
 * Convenience function for conditional rendering.
 */
export function categoryHasSubawards(
  category: string | null | undefined,
): boolean {
  return getCategoryConfig(category).hasSubawards
}

/**
 * Check if a category supports orders (child awards under an IDV).
 * Convenience function for conditional rendering.
 */
export function categoryHasOrders(
  category: string | null | undefined,
): boolean {
  return getCategoryConfig(category).hasOrders
}
